import { GatewayCheckoutPayload } from '../Types/PaymentType';

/**
 * Opening each provider's hosted checkout, and only this file knows how.
 *
 * <p>All four differ in kind, not just in detail:
 *
 * <ul>
 *   <li>Razorpay and Paytm hand back a callback when the payment finishes.
 *   <li>Paytm hands back a callback whose payload is a query string to be parsed.
 *   <li>Cashfree and PayU do not hand anything back in-page - the browser is redirected away, and the
 *       return route finishes the job.
 * </ul>
 *
 * <p>That last difference is why {@link openCheckout} returns void for PayU instead of resolving
 * with a callback, and why Cashfree's redirect target must resolve to the same return route: callers
 * must not assume a return value means the payment finished; the only authoritative outcome is what
 * the backend says after verification.
 */

/** Called with the provider's raw callback, already flattened to strings. */
export type CheckoutCallback = (params: Record<string, string>) => void;

/** Called when the customer dismisses the checkout without paying. */
export type CheckoutDismiss = () => void;

/**
 * Loads a provider's SDK once and resolves when it is usable.
 *
 * <p>Keyed on URL rather than provider, because two gateways can legitimately share a script host
 * and the browser caches the result anyway. A script that is already present resolves immediately, so
 * switching gateways mid-session does not re-download what is there.
 *
 * @throws when the script fails to load, so the caller can tell the customer rather than sitting on
 * a button that does nothing.
 */
export function loadGatewayScript(scriptUrl: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!scriptUrl) {
      reject(new Error('No checkout script URL was supplied for this gateway'));
      return;
    }

    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-gateway-sdk="${scriptUrl}"]`
    );
    if (existing) {
      if (existing.dataset.loaded === 'true') {
        resolve();
      } else {
        existing.addEventListener('load', () => resolve());
        existing.addEventListener('error', () => reject(new Error(`Failed to load ${scriptUrl}`)));
      }
      return;
    }

    const script = document.createElement('script');
    script.src = scriptUrl;
    script.async = true;
    script.dataset.gatewaySdk = scriptUrl;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => {
      // Removed so a retry can attempt the load again instead of resolving a permanently broken tag.
      script.remove();
      reject(new Error(`Failed to load ${scriptUrl}`));
    };

    document.body.appendChild(script);
  });
}

/**
 * Opens the selected provider's checkout.
 *
 * @param gateway    provider name, as returned by `GET /payments/gateways`
 * @param scriptUrl  where that provider's SDK lives
 * @param payload    provider-specific configuration from the create-payment response
 * @param onSuccess  raw callback, if this provider produces one
 * @param onDismiss  customer closed the checkout without paying
 * @param onReady    the checkout UI is on screen; used to clear the loading state
 */
export async function openCheckout(
  gateway: string,
  scriptUrl: string,
  payload: GatewayCheckoutPayload,
  onSuccess: CheckoutCallback,
  onDismiss: CheckoutDismiss,
  onReady?: () => void
): Promise<void> {
  await loadGatewayScript(scriptUrl);

  switch (gateway) {
    case 'RAZORPAY':
      openRazorpay(payload, onSuccess, onDismiss, onReady);
      return;
    case 'CASHFREE':
      // Awaited, not fired and forgotten: a checkout that fails to open has to reject here so the
      // caller's catch can show Cashfree's reason, rather than becoming an unhandled rejection.
      return openCashfree(payload, onReady);
    case 'PAYTM':
      openPaytm(payload, onSuccess, onReady);
      return;
    case 'PAYU':
      submitPayuForm(payload);
      return;
    default:
      throw new Error(`Unsupported payment gateway: ${gateway}`);
  }
}

/**
 * Razorpay: an SDK instance whose `handler` fires on a completed payment and whose `modal.ondismiss`
 * fires when the customer backs out.
 *
 * <p>The publishable key now comes from the backend's checkout payload rather than a build-time
 * environment variable - which is what makes the key editable by an admin instead of by a redeploy.
 */
function openRazorpay(
  payload: GatewayCheckoutPayload,
  onSuccess: CheckoutCallback,
  onDismiss: CheckoutDismiss,
  onReady?: () => void
): void {
  if (!window.Razorpay) {
    throw new Error('Razorpay checkout is unavailable');
  }

  const options = {
    key: String(payload.key ?? ''),
    amount: Number(payload.amount ?? 0),
    currency: String(payload.currency ?? 'INR'),
    order_id: String(payload.order_id ?? ''),
    name: 'Nexgent',
    description: String(payload.description ?? 'Subscription purchase'),
    // Fires when the provider considers the payment done. Unverified - the backend decides.
    handler: (response: Record<string, unknown>) => {
      onSuccess(flatten(response));
    },
    modal: {
      ondismiss: () => onDismiss(),
    },
    prefill: {},
    theme: { color: '#4F46E5' },
  };

  const rzp = new window.Razorpay(options);
  rzp.open();
  onReady?.();
}

/**
 * Cashfree: `Cashfree({ mode }).checkout({ paymentSessionId })` - and the browser leaves the page.
 *
 * <p>Cashfree's v3 SDK is not callback-based like Razorpay's. `checkout()` returns a promise that
 * resolves with `{ redirect: true }` when the customer is on their way to the gateway, and with
 * `{ error }` when the checkout could not be opened. The payment's outcome only exists once the
 * browser comes back to the return route, which is why `onSuccess` is deliberately never called from
 * here: a payment that has merely started is not a payment that succeeded, and only the backend may
 * say otherwise.
 *
 * <p>That makes Cashfree a redirect provider like PayU, which is why the return URL below is not
 * optional - without it the customer pays and lands nowhere.
 */
async function openCashfree(
  payload: GatewayCheckoutPayload,
  onReady?: () => void
): Promise<void> {
  if (!window.Cashfree) {
    throw new Error('Cashfree checkout is unavailable');
  }

  const paymentSessionId = String(payload.payment_session_id ?? '');
  if (!paymentSessionId) {
    throw new Error('Cashfree did not return a payment session for this order');
  }

  const cashfree = window.Cashfree({ mode: String(payload.mode ?? 'sandbox') });

  let result: { error?: { message?: string }; redirect?: boolean } | undefined;
  try {
    result = await cashfree.checkout({
      paymentSessionId,
      // Built from the browser's own origin rather than taken from server config. The browser is the
      // only party that knows where it actually is, and a return URL pointing at the wrong host
      // strands the customer on a dead page *after* their money has left. The backend also writes this
      // into order_meta.return_url; the two must agree, and the browser's origin is the harder half to
      // get wrong because it cannot be stale.
      //
      // `{order_id}` is mandatory for Cashfree: it substitutes the real order id into that placeholder
      // on the way back. It does not append one. Omit it and the customer returns to this route with
      // no order reference, which can only be reported as "not confirmed" - after they have paid.
      returnUrl: `${window.location.origin}/payment/return?gateway=CASHFREE&order_id={order_id}`,
      // Same tab, so the return route lands on a real app page rather than a popup the recruiter may
      // have already closed.
      redirectTarget: '_self',
    });
  } catch (error: any) {
    // Reached when the SDK itself throws. Released here, and deliberately not routed through
    // onDismiss: that cancels the payment server-side and reports "Cancelled", which would replace
    // the actual reason with a misleading one.
    onReady?.();
    throw error;
  }

  if (result?.error) {
    onReady?.();
    throw new Error(result.error.message || 'Cashfree could not open the checkout');
  }

  // `redirect: true` - the browser is navigating away to the gateway. onReady is intentionally NOT
  // called here: releasing the lock would let a second click create a second order while the first is
  // still in flight. The page is about to go away, so the locked button costs nothing.
}

/**
 * Paytm: one `PaytmCheckoutJS.init` with a config object whose success callback receives a raw
 * query string, which has to be parsed before anything useful can be read from it.
 */
function openPaytm(
  payload: GatewayCheckoutPayload,
  onSuccess: CheckoutCallback,
  onReady?: () => void
): void {
  if (!window.PaytmCheckoutJS) {
    throw new Error('Paytm checkout is unavailable');
  }

  window.PaytmCheckoutJS.init({
    mode: String(payload.mode ?? 'staging'),
    mid: String(payload.mid ?? ''),
    orderId: String(payload.orderId ?? ''),
    txnToken: String(payload.txnToken ?? ''),
    amount: String(payload.amount ?? ''),
    websiteName: String(payload.websiteName ?? 'WEBSTAGING'),
    successUrl: String(window.location.origin),
    failureUrl: String(window.location.origin),
    onSuccess: (response: string) => {
      onSuccess(parseCallbackQuery(response));
    },
    onFailure: () => {
      onReady?.();
    },
  });

  onReady?.();
}

/**
 * PayU: a real form POST to a hosted URL.
 *
 * <p>Not a redirect the app controls - the browser leaves, PayU collects the payment, and PayU sends
 * the customer back to the `surl` the backend put in the posted fields. So no callback fires here,
 * and the return lands on the return URL with PayU's response as query parameters for the backend to
 * verify.
 */
function submitPayuForm(payload: GatewayCheckoutPayload): void {
  const checkoutUrl = payload.checkoutUrl;
  const fields = payload.fields;

  if (!checkoutUrl || !fields) {
    throw new Error('PayU checkout is missing its form configuration');
  }

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = String(checkoutUrl);
  // Named so the browser's own "are you sure you want to leave?" prompt covers the submit, which a
  // programmatic click would bypass.
  form.setAttribute('data-gateway', 'payu');

  Object.entries(fields).forEach(([name, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = String(value);
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
}

/**
 * Flattens a provider callback into plain strings.
 *
 * <p>The verification endpoint takes `Record<string, string>`, and none of these SDKs produce only
 * strings - Razorpay hands back a typed object and Cashfree a positional pair - so everything is
 * stringified once, here, rather than in each provider's branch.
 */
function flatten(source: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};
  Object.entries(source ?? {}).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    result[key] = typeof value === 'object' ? JSON.stringify(value) : String(value);
  });
  return result;
}

/**
 * Parses Paytm's success payload, which arrives as a query string rather than an object.
 *
 * <p>Falls back to parsing the current URL, because Paytm also supports a full-page return that
 * delivers the same values as query parameters.
 */
function parseCallbackQuery(response: string): Record<string, string> {
  const query = response && response.includes('=')
    ? response
    : window.location.search.replace(/^\?/, '');

  const params = new URLSearchParams(query);
  const result: Record<string, string> = {};
  params.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}